import ConfirmPopover from '@/components/common/ConfirmPopover';
import List from '@/components/common/List';
import ListArrows from '@/components/common/ListArrows';
import PromptListPromptPickerDialog from '@/components/nodes/settings/prompt/PromptListPromptPickerDialog';
import { useApplication } from '@/hooks/useApplication';
import { emptyValue } from '@/types/detail';
import { VariableRef } from '@/types/variable';
import { isNumber, isVariable } from '@/util/typeGuards';
import { variablesListUtil } from '@/util/validation/variableslist';
import ClearIcon from '@mui/icons-material/Clear';
import { Button, Card, ListItem, ListItemText, Stack, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { ParamProps } from './ParamRenderer';

const ParamPromptList: FC<ParamProps> = (props) => {
  const theme = useTheme();
  const { application } = useApplication();

  const [openDialog, setOpenDialog] = useState(false);
  const [initialValue, setInitialValue] = useState<(VariableRef | emptyValue)[] | undefined>();

  const removeEntry = (indexToRemove: number, values: (VariableRef | emptyValue)[]) => {
    const newInitialValue = values.filter((_, index) => index !== indexToRemove);
    props.onChange([props.configuration.value, newInitialValue]);
  };

  useEffect(() => {
    setInitialValue(variablesListUtil().getValue(props.configuration, props.initialValue));
  }, [props.initialValue]);

  return (
    <Card
      variant='outlined'
      sx={{
        flexGrow: 1,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        ...(props.errors?.length && { borderColor: theme.palette.error.main })
      }}
    >
      <List subheader={props.configuration.label + (props.configuration.required ? ' *' : '')} sx={{ width: '100%' }}>
        {initialValue?.map((val, index) => {
          const isPrompt = isNumber(val);
          const selectedPrompt = isPrompt ? application?.prompts?.find((prompt) => prompt.id === val) : undefined;
          const itemText = isPrompt
            ? selectedPrompt?.label || selectedPrompt?.file || ''
            : isVariable(val)
              ? val.name
              : 'value';

          return (
            <ListItem key={`${val}.${index}`}>
              <ListItemText
                primary={
                  <Stack direction='row' justifyContent='space-between' alignItems='center' pr={1}>
                    {selectedPrompt?.promptGroup
                      ? `${application.promptGroups.find((group) => group.id === selectedPrompt?.promptGroup)?.name} / `
                      : ''}
                    {itemText}
                    <code>{isPrompt ? 'prompt' : 'promptlist'}</code>
                  </Stack>
                }
              />
              <ListArrows
                data={initialValue}
                index={index}
                onChange={(result) => props.onChange([props.configuration.value, result])}
              />
              <ConfirmPopover
                onConfirm={() => removeEntry(index, initialValue)}
                icon={<ClearIcon />}
                text={
                  <>
                    Remove <code>{itemText}</code> from list?
                  </>
                }
              />
            </ListItem>
          );
        })}
      </List>
      <Button variant='contained' color='primary' onClick={() => setOpenDialog(true)}>
        Add
      </Button>
      <PromptListPromptPickerDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={(newPrompt) => {
          props.onChange([
            props.configuration.value,
            [
              ...(initialValue || []),
              isNumber(newPrompt) ? newPrompt : { name: newPrompt.name, scope: newPrompt.scope }
            ]
          ]);
        }}
        subroutineId={props.parentId}
      />
    </Card>
  );
};

export default ParamPromptList;
